import { Box3, Mesh, Object3D } from "three";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { ICollidable } from "../../../../../../three/physics/ICollidable";
import { PhysicsLayers } from "../../../../../../PhysicsLayers";
import { Physics } from "../../../../../../three/physics/Physics";
import { Ticker } from "pixi.js";
import { Level } from "../../Level";
import { Event } from "event-system";
import { BaseMole } from "../mole/BaseMole";
import { ExplosionParticle } from "../particles/ExplosionParticle";
import { BatchedRenderer } from "three.quarks";

export class Mine extends Object3D implements ICollidable {
    private mine: Mesh;
    private parachute: Object3D;
    private particle: ExplosionParticle;
    private readonly _boundingBox: Box3;
    public get boundingBox(): Box3 {
        return this._boundingBox.copy(this.mine.geometry.boundingBox).applyMatrix4(this.matrixWorld);
    }
    private mineTriggerTimeout: number = 1500;

    public get collidesWithLayer(): number {
        return 1 << PhysicsLayers.Layer1 | 1 << PhysicsLayers.Layer2;
    }
    public readonly exploded: Event<Mine>;
    private moleTarget: BaseMole;

    public constructor(private readonly gameResources: GameResourcesPackage, private readonly particlesBatchRenderer: BatchedRenderer) {
        super();
        this.exploded = new Event();
        this.mine = clone(gameResources.assets_game_Boosters_glb.scene.children[0]) as Mesh;
        this.parachute = clone(gameResources.assets_game_Boosters_glb.scene.children[1]) as Mesh;
        this.add(this.mine, this.parachute);
        this.scale.set(2, 2, 2);
        this._boundingBox = new Box3().setFromObject(this.mine);
        Physics.add(this);
        Ticker.shared.add(this.flyDown, this);
    }

    public onCollision?(other: ICollidable): void {
        if (other instanceof Mine) {
            return;
        }
        Ticker.shared.remove(this.flyDown, this);
        this.removeParachute();
        if (other instanceof Level) {
            this.position.set(0, 0, 0);
        } else if (other instanceof BaseMole) {
            Physics.remove(this);
            other.setupMineOnTop(this);
            this.moleTarget = other;
            Ticker.shared.add(this.triggered, this);
        }
    }

    private flyDown(): void {
        this.position.y -= 0.05 * Ticker.shared.deltaTime;
    }

    private removeParachute(): void {
        if (this.parachute == null) {
            console.warn("Parachute already removed");
            return;
        }
        this.remove(this.parachute);
    }

    private triggered(): void {
        this.mineTriggerTimeout = Math.max(this.mineTriggerTimeout - Ticker.shared.deltaMS, 0);
        if (this.mineTriggerTimeout == 0) {
            Ticker.shared.remove(this.triggered, this);
            this.explode();
        }
    }

    private async explode(): Promise<void> {
        this.particle = new ExplosionParticle(this.gameResources, this.particlesBatchRenderer);
        this.add(this.particle);
        this.remove(this.mine);
        this.moleTarget?.takeDamage(200);
        this.exploded.emit(this);
    }

    public destroy(): void {
        // 1. Remove from physics world
        Physics.remove(this);

        // 2. Remove ticker callbacks
        Ticker.shared.remove(this.flyDown, this);
        Ticker.shared.remove(this.triggered, this);

        // 3. Stop particle system if active
        if (this.particle) {
            this.remove(this.particle);
            this.particle = null!;
        }

        // 4. Remove parachute if still present
        if (this.parachute && this.children.includes(this.parachute)) {
            this.remove(this.parachute);
        }

        // 5. Remove mine mesh if still present
        if (this.mine && this.children.includes(this.mine)) {
            this.remove(this.mine);
        }

        // 6. Unsubscribe all events
        this.exploded.removeAll();

        // 7. Remove from parent
        this.parent?.remove(this);

        // 8. Null out references to help GC
        this.moleTarget = null!;
    }
}
